import React from "react";
import { Helmet } from "react-helmet";
import {
  Box,
  Container,
  Typography,
  Stack,
  Divider,
  Button,
  alpha,
  useTheme
} from "@mui/material";
import PrintIcon from "@mui/icons-material/Print";
import { ACCENT, MEASURE } from "../data/studioStory";
import Navigation from "../components/Navigation";
import Footer from "../components/Footer";
import PageTransition from "../components/PageTransition";
import ScrollReveal from "../components/ScrollReveal";

function ResumePage() {
  const theme = useTheme();

  return (
    <PageTransition>
      <Helmet>
        <title>Résumé – David Kooi</title>
        <link rel="canonical" href="https://davidkooi.com/resume/" />
        <meta name="description" content="David Kooi's professional experience and education - independent AI-orchestration consultant, founder of Skylark Creations, and co-founder of Jointly." />
      </Helmet>
      
      <Box sx={{ minHeight: "100vh", backgroundColor: theme.palette.background.default }}>
        <Navigation />
      
      <Container component="main" id="main" maxWidth="md" sx={{ py: 6 }}>
        {/* Prose measure — see AboutPage. */}
        <Stack spacing={4} sx={{ maxWidth: MEASURE, mx: "auto", width: "100%" }}>
          <ScrollReveal>
            <Typography variant="h3" component="h1" sx={{ fontWeight: 800, textAlign: "center" }}>
              Résumé
            </Typography>
            {/* ponytail: window.print() + a print stylesheet, not a PDF pipeline.
                "Save as PDF" is already in every browser's print dialog, and a
                generated PDF would be a second copy to keep in sync. */}
            <Box sx={{ textAlign: "center", mt: 2 }} className="no-print">
              <Button
                variant="outlined"
                startIcon={<PrintIcon />}
                onClick={() => window.print()}
                sx={{
                  color: "text.primary",
                  borderColor: theme.palette.divider,
                  "&:hover": { borderColor: ACCENT, backgroundColor: alpha(ACCENT, 0.08) },
                }}
              >
                Print or save as PDF
              </Button>
            </Box>
          </ScrollReveal>

          <ScrollReveal delay={0.1}>
            <Box>
              <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
                Work Experience
              </Typography>
              
              <Stack spacing={3}>
              <Box>
                <Typography variant="h6" component="h3" sx={{ fontWeight: 600 }}>
                  Independent AI-Orchestration Consultant
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Denver, CO (2026 – Present)
                </Typography>
                <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
                  Runs client engagements end to end on the same agent-fleet operating system that runs Skylark – one human orchestrating planning, verification, and delivery across a fleet of AI agents.
                </Typography>
                <Box component="ul" sx={{ pl: 3, mt: 1, mb: 0, '& li': { mb: 0.5 } }}>
                  <Typography component="li" variant="body2">
                    Async-first engagements: no standing meetings – artifacts land in the client's inbox, and client reactions routinely return as verified, deployed changes the same or next day
                  </Typography>
                  <Typography component="li" variant="body2">
                    Sustained a verified daily ship cadence – on one engagement, 6–11 verified merges per day was the normal rate over a two-week stretch
                  </Typography>
                  <Typography component="li" variant="body2">
                    Spec-and-kill-gate before code: decide and spec first, with the opening milestone designed to prove or kill the core technical bet before any build-out
                  </Typography>
                  <Typography component="li" variant="body2">
                    Adversarial cross-model verification as a release gate – a second model family is prompted to refute the work before the client sees it; this gate caught a second bypass in an already-reviewed fix
                  </Typography>
                  <Typography component="li" variant="body2">
                    Every client point tracked in a dispositioned feedback ledger: faithful paraphrase, written ruling with reasoning, and a pointer to where it landed – disagreements argued in writing, never dropped
                  </Typography>
                  <Typography component="li" variant="body2">
                    Bills on engaged attention, not machine time – adding agent capacity never raises the invoice, and quiet weeks legitimately bill near zero
                  </Typography>
                </Box>
              </Box>

              <Box>
                <Typography variant="h6" component="h3" sx={{ fontWeight: 600 }}>
                  Skylark Creations LLC – Founder
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Denver, CO (2024 – Present)
                </Typography>
                <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
                  Founded a one-person, AI-native product studio that designs, builds, and ships apps from zero to live – using AI at every layer of the stack. Reasoning partners for architecture, coding agents for implementation, human advisors for taste and truth.
                </Typography>
                <Box component="ul" sx={{ pl: 3, mt: 1, mb: 0, '& li': { mb: 0.5 } }}>
                  <Typography component="li" variant="body2">
                    Shipped 10+ live production apps across iOS, Android, and web – including Decomposer (AI planning), Agentic News (news intelligence), Agentic Directory (AI product curation), BuddhaUR (Buddhist learning), BloomEDU (46K+ wildflower species), Urban Journeys (themed city walks), FrameDial News (cited daily briefings), AniMoment, QuickBite, Quiet Tab, and Sagent – each instrumented with analytics, error tracking, and subscription infrastructure
                  </Typography>
                  <Typography component="li" variant="body2">
                    Built an agentic development workflow that lets a single founder approximate a small product and engineering team – using LLM-based coding agents, automated research, and scripted QA to move from idea to production in days instead of months
                  </Typography>
                  <Typography component="li" variant="body2">
                    Operates the portfolio as an orchestrated agent fleet: a manager AI runs each product's daily cycle over a shared message bus, daily reports are cold-audited against their own claims, and only true decisions reach the founder
                  </Typography>
                  <Typography component="li" variant="body2">
                    Actively developing additional products including Syntopica (cross-book synthesis), Frolic (outdoor activity recommendations), Dreamflyer (meditative flying game), and Urban Beaver (puzzle game)
                  </Typography>
                  <Typography component="li" variant="body2">
                    Products span news intelligence, spiritual practice, botanical education, urban exploration, group decision-making, and developer tools – with paying subscribers acquired entirely through organic discovery
                  </Typography>
                </Box>
              </Box>

              <Box>
                <Typography variant="h6" component="h3" sx={{ fontWeight: 600 }}>
                  Jointly – Co-founder, CEO & Chief Product Officer
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Denver, CO (2018 – 2025)
                </Typography>
                <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
                  Led all product and organizational strategy for a purpose-driven wellness technology platform, integrating AI/ML to deliver personalized cannabis consumption experiences. Dissolved the company in 2025 &ndash; we built for precision in a market that wanted speed, and arrived early to an industry that wasn&rsquo;t ready to buy data. The dataset and the theory it produced outlived it.
                </Typography>
                <Box component="ul" sx={{ pl: 3, mt: 1, mb: 0, '& li': { mb: 0.5 } }}>
                  <Typography component="li" variant="body2">
                    Conceived and launched Spark, first AI-powered virtual assistant for personalized recommendations
                  </Typography>
                  <Typography component="li" variant="body2">
                    Grew app to 500,000+ downloads, tens of thousands of MAUs, achieving 35% 30-day retention
                  </Typography>
                  <Typography component="li" variant="body2">
                    Engineered proprietary algorithms using LLMs and feedback loops from millions of interactions
                  </Typography>
                  <Typography component="li" variant="body2">
                    Led remote, international team of 15+ across engineering, data science, design, and marketing
                  </Typography>
                  <Typography component="li" variant="body2">
                    Authored "Theory of Purposeful Cannabis Consumption," the company's framing of cannabis as a purposeful wellness tool
                  </Typography>
                </Box>
              </Box>

              <Box>
                <Typography variant="h6" component="h3" sx={{ fontWeight: 600 }}>
                  Santa Monica Mountains Cyclery – Founder & Owner
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Los Angeles, CA (2010 – 2021)
                </Typography>
                <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
                  Built one of Southern California&rsquo;s top cycling retailers over a decade; co-founded Jointly in 2018 and sold the shop to Trek Bicycle in 2021.
                </Typography>
                <Box component="ul" sx={{ pl: 3, mt: 1, mb: 0, '& li': { mb: 0.5 } }}>
                  <Typography component="li" variant="body2">
                    Achieved 10 consecutive years of revenue and profitability growth
                  </Typography>
                  <Typography component="li" variant="body2">
                    Maintained 4.92 average rating across 1,200+ five-star reviews
                  </Typography>
                  <Typography component="li" variant="body2">
                    Launched drop-ship partnerships, increasing online sales 60% YoY
                  </Typography>
                  <Typography component="li" variant="body2">
                    Negotiated the exit to Trek Bicycle
                  </Typography>
                </Box>
              </Box>

              <Box>
                <Typography variant="h6" component="h3" sx={{ fontWeight: 600 }}>
                  McMaster-Carr – Director of Operations & Project Leader
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Los Angeles, CA (2002 – 2009)
                </Typography>
                <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
                  Oversaw a $1B+ distribution operation with 300+ staff.
                </Typography>
                <Box component="ul" sx={{ pl: 3, mt: 1, mb: 0, '& li': { mb: 0.5 } }}>
                  <Typography component="li" variant="body2">
                    Led implementation of parametric search for e-commerce
                  </Typography>
                  <Typography component="li" variant="body2">
                    Managed 10,000+ daily shipments achieving 99.4% fulfillment accuracy
                  </Typography>
                  <Typography component="li" variant="body2">
                    Devised same-day delivery programs, increasing metro order volume by 25%
                  </Typography>
                </Box>
              </Box>
            </Stack>
            </Box>
          </ScrollReveal>

          <Divider sx={{ borderColor: theme.palette.divider }} />

          <ScrollReveal delay={0.2}>
            <Box>
              <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
                Education
              </Typography>
            
            {/* Reverse-chronological, matching Work Experience above. It used to
                run 2002 → 1996 → 2016, which buried the most interesting entry:
                a CS degree finished at 43, after the MBA. A due-diligence reader
                had to reorder these by hand to see the reskilling. */}
            <Stack spacing={2}>
              <Box>
                <Typography variant="h6" component="h3" sx={{ fontWeight: 600 }}>
                  Oregon State University
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  B.S. in Computer Science (2016)
                </Typography>
              </Box>

              <Box>
                <Typography variant="h6" component="h3" sx={{ fontWeight: 600 }}>
                  University of Chicago Booth School of Business
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  MBA, Strategy, Finance, and Entrepreneurship (2002)
                </Typography>
              </Box>

              <Box>
                <Typography variant="h6" component="h3" sx={{ fontWeight: 600 }}>
                  Northwestern University
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  B.A. in Economics & Statistics, Minor in Mathematics (1996)
                </Typography>
              </Box>
            </Stack>
            </Box>
          </ScrollReveal>

          <Divider sx={{ borderColor: theme.palette.divider }} />

          <ScrollReveal delay={0.3}>
            <Box>
              <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
                Skills & Expertise
              </Typography>
              
              <Stack direction={{ xs: "column", sm: "row" }} spacing={4}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h6" component="h3" gutterBottom sx={{ fontWeight: 600 }}>
                    Core Competencies
                  </Typography>
                  <Typography variant="body2" paragraph>
                    • AI Agent Orchestration & Multi-Agent Systems<br />
                    • Product Strategy & Roadmapping<br />
                    • AI-Native Product Operations<br />
                    • Cross-Functional Team Leadership<br />
                    • Data-Driven Decision Making<br />
                    • Rapid Prototyping & Iteration<br />
                    • Go-to-Market & Growth Strategy<br />
                    • Strategic Partnerships<br />
                    • High-Level Stakeholder Management
                  </Typography>
                </Box>
                
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h6" component="h3" gutterBottom sx={{ fontWeight: 600 }}>
                    Technical Skills
                  </Typography>
                  <Typography variant="body2" paragraph>
                    <strong>AI Agent Systems:</strong> Multi-agent orchestration in production &ndash; one specialist agent per product across the portfolio, each running its own daily cycle, one human orchestrating &ndash; agent message buses and phase-gated workflows, Claude Code and agent SDKs, MCP, RAG pipelines, tool-use agents, browser and mobile automation agents (Playwright, Stagehand, Maestro).<br />
                    <strong>LLM Engineering & Evaluation:</strong> LLM-as-judge ensembles with position-swap debiasing, shadow A/B prompt experiments, noise-floor-calibrated evals, adversarial review gates, synthetic-persona user testing, prompt and schema design, structured outputs, semantic search (pgvector, Weaviate, Chroma), cross-provider model audits and per-token cost observability (Anthropic, OpenAI), Python (NLP, pandas), XGBoost, vision models.<br />
                    <strong>Web & Mobile:</strong> JavaScript, TypeScript, Next.js 15/16, React 19, Node.js, Express, Fastify, Swift/SpriteKit/SwiftUI, React Native + Expo, Tailwind CSS, shadcn/ui, Zustand, Framer Motion, Mapbox GL JS.<br />
                    <strong>Data & Cloud:</strong> PostgreSQL (Neon) + Drizzle ORM, MongoDB, Prisma, Redis, Supabase Auth, AWS (ECS, Fargate, S3), Stripe, BullMQ, Docker.<br />
                    <strong>Development:</strong> Microservices, REST/JSON APIs, CI/CD (GitHub Actions), real-time features (SSE/WebSockets), Chrome Extensions (Manifest V3), Agile/Scrum.<br />
                    <strong>Tools & Automation:</strong> Claude Code, Cursor, n8n, Render, Vercel, Sentry, Amplitude, PostHog, Doppler, Firecrawl, SendGrid, ElevenLabs.
                  </Typography>
                </Box>
              </Stack>
            </Box>
          </ScrollReveal>
        </Stack>
      </Container>

      <Footer />
    </Box>
  </PageTransition>
  );
}

export default ResumePage;
